import * as React from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { InteractionStatus, InteractionRequiredAuthError, AccountInfo, EventType, IPublicClientApplication } from '@azure/msal-browser';
import { setupAxiosInterceptor } from '../helpers/axiosInstance';
import { GetHostHref } from '../helpers/SettingsMisc';
import { IsVerbose } from '../helpers/Console';
import { blacklistToken } from '../helpers/PostToAdminApi';

interface AuthContextType {
  handleLogin: (scopes?: string[]) => Promise<string>;
  handleLogout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  consentScopes: (scopes: string[]) => Promise<boolean>;
  accessToken: string | null;
  scp: string[]; // Granted scopes from login response
  isAuthenticated: boolean;
  isSigningIn: boolean;
  isInitializing: boolean; // New flag to indicate token acquisition in progress
  account: AccountInfo | null;
}

const app_scopes = import.meta.env.VITE_SCOPE?.split(' ') || [''];
const default_graph_scopes = import.meta.env.VITE_DEFAULT_GRAPH_SCOPES?.split(' ') || ['user.read', 'organization.read.all'];

export const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

/**
 * Wait for any in-progress MSAL interaction to complete before proceeding.
 * Uses MSAL event callbacks so we don't poll a stale closure value.
 */
const waitForNoInteraction = (msalInstance: IPublicClientApplication, timeoutMs = 10000): Promise<void> => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for MSAL interaction to complete'));
    }, timeoutMs);

    const callbackId = msalInstance.addEventCallback((event) => {
      // These events signal that an interactive flow has finished
      if (
        event.eventType === EventType.HANDLE_REDIRECT_END ||
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.LOGIN_FAILURE ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE
      ) {
        cleanup();
        resolve();
      }
    });

    const cleanup = () => {
      clearTimeout(timeout);
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  });
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [accessToken, setAccessToken] = React.useState<string | null>(null);
  const [scp, setScp] = React.useState<string[]>(() => {
    const stored = localStorage.getItem('scp');
    return stored ? JSON.parse(stored) : [];
  });
  const [isSigningIn, setIsSigningIn] = React.useState<boolean>(false);
  const isSigningInRef = React.useRef(false); // Synchronous flag to prevent race conditions
  const [isInitializing, setIsInitializing] = React.useState<boolean>(true); // Start as true
  const navigate = useNavigate();

  const account = accounts[0] || null;

  // Get access token with automatic refresh handling by MSAL
  // Always uses APP_SCOPES only
  const getAccessToken = React.useCallback(async (): Promise<string> => {
    if (inProgress !== InteractionStatus.None) {
      IsVerbose() && console.log('🔄 Authentication in progress, waiting for completion...');
      try {
        await waitForNoInteraction(instance);
      } catch {
        console.error('❌ Timed out waiting for MSAL interaction');
        return '';
      }
    }

    if (!account) {
      IsVerbose() && console.log('❌ No account found');
      return '';
    }

    // Always use APP_SCOPES for accessToken
    const tokenScopes = app_scopes;

    try {
      // MSAL automatically handles token refresh and caching
      // Use blank.html as redirectUri so the hidden iframe is allowed by X-Frame-Options: SAMEORIGIN
      const response = await instance.acquireTokenSilent({
        account: account,
        scopes: tokenScopes,
        redirectUri: GetHostHref() + '/blank.html',
      });

      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      // Handle interaction required errors (e.g., consent, MFA)
      if (error instanceof InteractionRequiredAuthError) {
        IsVerbose() && console.log('🔐 Interaction required, redirecting for login...');
        // Use redirect to avoid Cross-Origin-Opener-Policy issues with popups
        await instance.acquireTokenRedirect({
          account: account,
          scopes: tokenScopes,
        });
        return ''; // Will redirect away, token acquired on return
      }

      console.error('❌ Token acquisition error:', error);
      navigate('/login');
      return '';
    }
  }, [instance, account, inProgress, navigate]);

  const handleLogin = React.useCallback(async (newScopes?: string[]): Promise<string> => {
    // Use ref for synchronous check to prevent race conditions with rapid clicks
    if (isSigningInRef.current || inProgress !== InteractionStatus.None) {
      IsVerbose() && console.log('⚠️ Authentication already in progress');
      return '';
    }

    // Set both ref and state synchronously
    isSigningInRef.current = true;
    setIsSigningIn(true);

    try {
      // Use DEFAULT_GRAPH_SCOPES if no scopes provided
      const loginScopes = newScopes || default_graph_scopes;

      // Use redirect-based login to avoid Cross-Origin-Opener-Policy issues with popups.
      // After redirect, MSAL handles the response via handleRedirectPromise() in MsalContext.
      await instance.loginRedirect({
        scopes: [...loginScopes, ...app_scopes],
        prompt: "select_account",
      });

      // loginRedirect navigates away, so code below won't execute until redirect back.
      // Token acquisition happens in the useEffect that handles page reload.
      return '';
    } catch (error) {
      console.error('❌ Login failed:', error);
      isSigningInRef.current = false;
      setIsSigningIn(false);
      navigate('/login');
      throw error;
    }
  }, [instance, inProgress, navigate]);

  // Consent to additional Graph scopes via popup (does not navigate away)
  const consentScopes = React.useCallback(async (scopes: string[]): Promise<boolean> => {
    if (inProgress !== InteractionStatus.None) {
      IsVerbose() && console.log('⚠️ Authentication already in progress');
      return false;
    }

    if (!account) {
      IsVerbose() && console.log('❌ No account found for consent');
      return false;
    }

    try {
      const response = await instance.acquireTokenPopup({
        account: account,
        scopes: [...scopes, ...app_scopes],
        redirectUri: GetHostHref() + '/blank.html',
      });

      if (response?.scopes) {
        const updatedScopes = response.scopes;
        setScp(updatedScopes);
        localStorage.setItem('scp', JSON.stringify(updatedScopes));
        IsVerbose() && console.log('✅ Consent granted via popup, scopes updated');
      }
      return true;
    } catch (error) {
      console.error('❌ Consent popup failed:', error);
      return false;
    }
  }, [instance, account, inProgress]);

  const handleLogout = React.useCallback(async () => {
    try {
      isSigningInRef.current = false;
      setIsSigningIn(false);
      // Blacklist token server-side before clearing local state
      if (accessToken) {
        await blacklistToken(accessToken).catch((err) =>
          console.error('Failed to blacklist token:', err)
        );
      }
      setAccessToken(null);
      setScp([]);
      localStorage.removeItem('scp');
      sessionStorage.clear();
      await instance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: '/login',
      });
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    }
  }, [instance, account, navigate, accessToken]);

  // Initialize axios interceptor with auth functions
  React.useEffect(() => {
    const logoutAndNavigate = () => {
      setAccessToken(null);
      navigate('/login');
    };

    const cleanup = setupAxiosInterceptor(getAccessToken, logoutAndNavigate);
    IsVerbose() && console.log('✅ Axios interceptor configured for 401 handling');

    // Clean up interceptor on unmount or when dependencies change
    return cleanup;
  }, [getAccessToken, navigate]);

  // Initialize token on mount - try to acquire token silently on page reload when user is already authenticated
  React.useEffect(() => {
    const acquireTokenOnLoad = async () => {
      // Wait for MSAL to finish any in-progress operations
      if (inProgress !== InteractionStatus.None) {
        IsVerbose() && console.log('⏳ MSAL interaction in progress, waiting...');
        return;
      }

      // If we have an account but no token, force a fresh token on page load
      if (account && !accessToken) {
        IsVerbose() && console.log('🔄 Page reload detected, acquiring fresh token...');
        try {
          const response = await instance.acquireTokenSilent({
            account: account,
            scopes: app_scopes,
            redirectUri: GetHostHref() + '/blank.html',
            forceRefresh: true, // Force fresh token on page load per Microsoft best practices
          });
          if (response.accessToken) {
            setAccessToken(response.accessToken);
            IsVerbose() && console.log('✅ Fresh token acquired on reload');
          } else {
            IsVerbose() && console.log('⚠️ Silent token acquisition returned empty, user may need to login again');
          }
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            IsVerbose() && console.log('🔐 Interaction required on reload, redirecting...');
            await instance.acquireTokenRedirect({
              account: account,
              scopes: app_scopes,
            });
          } else {
            console.error('❌ Failed to acquire token on reload:', error);
          }
        } finally {
          setIsInitializing(false);
        }
      } else if (!account) {
        // No account means user is not logged in
        IsVerbose() && console.log('❌ No account found, user needs to login');
        setIsInitializing(false);
      } else if (accessToken) {
        // Already have token, done initializing
        IsVerbose() && console.log('✅ Token already available');
        setIsInitializing(false);
      }
    };

    acquireTokenOnLoad();
  }, [account, accessToken, inProgress, instance]);

  // Reset signing flag when user comes back from redirect
  React.useEffect(() => {
    if (account && isSigningIn) {
      isSigningInRef.current = false;
      setIsSigningIn(false);
    }
  }, [account, isSigningIn]);

  // Safety timeout: if initialization takes too long (e.g., iframe blocked),
  // stop waiting so the user can interact with the login page
  React.useEffect(() => {
    if (!isInitializing) return;

    const timeout = setTimeout(() => {
      if (isInitializing) {
        console.warn('⚠️ Auth initialization timed out after 10s, stopping wait');
        setIsInitializing(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isInitializing]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        scp,
        handleLogin,
        handleLogout,
        getAccessToken,
        consentScopes,
        isAuthenticated,
        isSigningIn,
        isInitializing,
        account
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
